import * as  React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import * as _ from 'lodash';
import { ICohort } from '../../../../models/ICohort';
import { Environment } from '../../../../const/environment';
import { IProject } from '../../../../models/IProject';
import PopupWrapper from '../../../components/Popup/PopupWrapper';
import TextFieldWrapper from '../../../components/TextFieldWrapper/TextFieldWrapper';
import { IStore } from '../../../../redux/initialState';
import platformNames from '../../../../const/platformNames';
import asyncDispatch from '../../../../services/mini/asyncDispatch';
import * as actions from '../../../../redux/projects/actions.projects';
import { getStringOption } from '../getProjectOptions';
import { ALAN_EXTENSION_ALLOWED_SITES_OPT_KEY, ALAN_EXTENSION_BTN_OPTIONS_OPT_KEY, getDefaultOptionsValue } from '../defaultProjectOptions';
import copyTextToBuffer from '../../../../services/mini/copyTextToBuffer';
import ToasterService from '../../../../services/ToasterService';
import { getProjectOptionsByEnvironment } from '../../../../services/mini/getProjectOptionsByEnvironment';
import device from '../../../../services/mini/device';
import menuItems from '../../../../const/menuItems';
import PopupButtons from '../../../components/Popup/PopupButtons';
import PopupCancelButton from '../../../components/Popup/PopupCancelButton';
import { Button, ButtonSize, ButtonTheme } from '../../../components/Button/Button';
import { isValidJSON, smartJSONParse } from '../../../../services/mini/isValidJSON';

const DATA_API_HOST = process.env.DATA_API_HOST;

function getExtensionPluginUrl() {
    if (device.isFirefox()) {
        return 'https://addons.mozilla.org/en-US/firefox/addon/alan-ai-plugin/';
    }
    return 'https://chromewebstore.google.com/detail/alan-ai-plugin/khihmlmopianodljghhmmmbnjmjfkckj?hl=en-US&utm_source=ext_sidebar';
}


type AlanButtonExtensionFormProps = PropsFromRedux & {
    projectId: string
    project: IProject
    environment: Environment
    platform: string
    optionsMode: boolean
    selectedCohortId: string
    cohorts: ICohort[]
    onClose: () => void
};


type AlanButtonExtensionFormState = {
    allowedSites: string[]
    btnOptions: string
    isBtnOptionsShown: boolean
    btnOptionsErr?: string
    hasExtension?: boolean
}

class AlanButtonExtensionForm extends React.Component<AlanButtonExtensionFormProps, AlanButtonExtensionFormState> {

    _isMounted: boolean;

    constructor(props) {
        super(props);
        this.state = {
            ...this.fillState(props),
        };

    }

    componentDidMount() {
        this._isMounted = true;

        this.checkForExtension();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    checkForExtension = () => {
        this.setState({ hasExtension: document.body.getAttribute('has-alan-extension-button') === 'true' });
    }

    fillState = (props) => {

        const allowedSitesOption = getStringOption(props, ALAN_EXTENSION_ALLOWED_SITES_OPT_KEY);
        const btnOptions = getStringOption(props, ALAN_EXTENSION_BTN_OPTIONS_OPT_KEY);

        return {
            allowedSites: allowedSitesOption.length > 0 ? allowedSitesOption.split(',') : [''],
            btnOptions: btnOptions,
            isBtnOptionsShown: btnOptions?.length > 4,
            btnOptionsErr: null
        };
    }

    handleBtnOptionsChange = (e) => {
        let val = e.target.value;

        if (isValidJSON(val, true)) {
            this.setState({ btnOptionsErr: null });
        } else {
            this.setState({ btnOptionsErr: 'Options should be in the valid JSON format' });
        }

        this.setState({ btnOptions: val });
    }

    handleAllowedSiteChange = (e, i) => {
        const { allowedSites } = this.state;
        const newAllowedSites = [...allowedSites];
        newAllowedSites[i] = e.target.value;
        this.setState({ allowedSites: newAllowedSites });
    }

    handleAddWebsite = () => {
        this.setState({ allowedSites: [...this.state.allowedSites, ''] });
    }
    handleRemoveWebsite = (i) => {
        const { allowedSites } = this.state;
        const newAllowedSites = [...allowedSites];
        newAllowedSites[i] = null;
        this.setState({ allowedSites: newAllowedSites.filter(site => !!site) });
    }
    handleCopySettings = (i) => {
        const { allowedSites, btnOptions } = this.state;
        const { project, environment } = this.props;
        try {
            copyTextToBuffer(btoa(JSON.stringify({
                allowedSites,
                projectKey: `${project.projectId}/${environment}`,
                projectName: project.name,
                alanHostOption: DATA_API_HOST,
                btnSettingsOption: btnOptions === '' ? {} : JSON.stringify(smartJSONParse(btnOptions))
            })));
            ToasterService.showInfo({ title: `Application code copied to clipboard!` });
        } catch (error) {

        }
    }

    handleAddingBtnOptions = () => {
        this.setState({ isBtnOptionsShown: true });
    }

    handleSave = () => {
        const { allowedSites, btnOptions, btnOptionsErr } = this.state;

        if (btnOptionsErr) {
            ToasterService.showError({ title: 'Please fix errors in settings before saving' });
            return;
        }

        const cleanedAllowedSites = allowedSites?.map(s => s.trim()).filter(s => s);

        this.setState({ allowedSites: cleanedAllowedSites });

        this.setTextOption(ALAN_EXTENSION_ALLOWED_SITES_OPT_KEY, '', cleanedAllowedSites.join(','));
        this.setTextOption(ALAN_EXTENSION_BTN_OPTIONS_OPT_KEY, 'Settings were saved.', btnOptions);
    }

    setTextOption = (optionName, toastrMsg, value) => {
        const {
            project,
            platform,
            environment,
            cohorts,
            selectedCohortId,
            setProjectOptions
        } = this.props;

        const options = getProjectOptionsByEnvironment(project, platform, environment, cohorts, selectedCohortId);

        if (_.get(options[platform], optionName) === value) {
            return;
        }

        if (_.get(options[platform], optionName) === undefined && value === getDefaultOptionsValue(platform, optionName)) {
            return;
        }

        options[platform] = _.set(options[platform], optionName, value);

        setProjectOptions({ project, options, environment, cohorts, selectedCohortId }).then(() => {
            if (toastrMsg) {
                ToasterService.showInfo({ title: toastrMsg });
            }
        });
    }

    render() {
        const {
            platform,
            onClose,
        } = this.props;

        const isExtensionAvailable = device.isChrome() || device.isFirefox() || device.isEdge()

        const extUrl = getExtensionPluginUrl();

        const { allowedSites, hasExtension, btnOptions, isBtnOptionsShown, btnOptionsErr } = this.state;

        const addBtnIsDisabled = allowedSites.length > 0 ? allowedSites.filter(site => site.trim() === '').length > 0 : false;
        const allowedSitesOption = getStringOption(this.props, ALAN_EXTENSION_ALLOWED_SITES_OPT_KEY);
        const btnOptionsOption = getStringOption(this.props, ALAN_EXTENSION_BTN_OPTIONS_OPT_KEY);
        const savedSitesOption = allowedSitesOption.length > 0 ? allowedSitesOption.split(',') : [];


        const hasUnsavedChanges = allowedSites?.join(',') !== savedSitesOption?.join(',') ||
            btnOptionsOption !== btnOptions;
        const hasNotEmptyUrls = allowedSites?.map(site => site.trim()).join('').length > 0;


        return <PopupWrapper className="alan-button-extension-options alan-button-options">
            <div className="alan-button-extension-options_external-wrapper">
                <div className="popup-form_header popup-form_header_with-commands">
                    <div className={`popup-form_header_cmd`}
                        onClick={onClose}>
                        <i className="icon icon-alan-chevron"
                            style={{
                                transform: 'rotate(90deg)',
                                display: 'inline-block',
                                marginRight: '5px'
                            }} />
                        Back
                    </div>
                    <div>{`${platformNames[platform]} -  Alan Button Extension`}</div>
                    <div className="popup-form_header_cmd" >
                        <div className={`action-btn alan-button-extension-options_copy-settings-btn ${(!hasNotEmptyUrls || hasUnsavedChanges) && 'disabled'}`}
                            onClick={this.handleCopySettings}>
                            <i className="icon icon-alan-test-copy" />  Copy Application Code
                        </div>
                    </div>
                </div>
                <div className="popup-form_internal-wrapper alan-button-extension-options_internal-wrapper">

                    {isExtensionAvailable && <div className={`alan-button-extension-options_ext-info-header ${hasExtension && 'installed'}`}>
                        {hasExtension ? <span>Alan AI Plugin is installed.</span> : <span>
                            You don't have the Alan AI Plugin installed. Click <a href={extUrl}
                                target="_blank"
                                rel="noreferrer noopener">here</a> to install it.</span>}
                    </div>}

                    <div className="alan-button-extension-options_content-header">
                        <div>URLs</div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <div className={`alan-button-extension-options_save-btn ${!isBtnOptionsShown ? '' : 'disabled'}`} onClick={this.handleAddingBtnOptions}>
                                <i className="icon icon-alan-cog" />
                                Set additional options</div>
                            <div className={`alan-button-extension-options_save-btn ${hasUnsavedChanges ? '' : 'disabled'}`} onClick={this.handleSave}>
                                <i className="icon icon-alan-save-changes-new" />
                                Save</div>
                        </div>
                    </div>

                    <div className="alan-button-extension-options_content-cols">
                        <div className="alan-button-extension-options_content">
                            <div className="alan-button-extension-options_allowed-sites">
                                <div>
                                    Please input a segment of the website's URL to specify the location where the Alan Button should be integrated.
                                    Visit <a href={menuItems.browserPlugin.href}
                                        target="_blank"
                                        rel="noreferrer noopener">Browser Plugin</a> page to get more info.
                                </div>
                                {allowedSites.map((site, i) => {
                                    const websiteName = `website-url-${i}`;

                                    return <div key={i} className="alan-button-extension-options_allowed-site-row">
                                        <TextFieldWrapper
                                            name={websiteName}
                                            id={websiteName}
                                            placeholder="Website url segment"
                                            value={site}
                                            autoComplete="off"
                                            onChange={(e) => {
                                                this.handleAllowedSiteChange(e, i);
                                            }}
                                            onEnter={this.handleSave}
                                            onBlur={this.handleSave}
                                            skipOnChangeAfterOnBlur={true}
                                            height={30}
                                        />
                                        {allowedSites.length > 1 &&
                                            <div className="action-btn light-red-with-border  alan-button-extension-options_remove-website-btn"
                                                onClick={() => {
                                                    this.handleRemoveWebsite(i);
                                                }}>
                                                <i className="icon icon-alan-trash" />
                                            </div>}
                                    </div>;
                                })}
                            </div>
                            <div className={`action-btn light-blue-with-border alan-button-extension-options_add-website-btn ${addBtnIsDisabled && 'disabled'}`}
                                onClick={this.handleAddWebsite}>
                                + Add Website Segment
                            </div>
                        </div>

                        {isBtnOptionsShown &&
                            <div className="alan-button-extension-options_content">
                                <div>
                                    Additional settings: for example, to position the button you can set such options: {`{"right": "50px", "theme":"dark"}`}, to apply default options set it to: {`{}`}.
                                </div>
                                <div className="alan-button-extension-options_btn-options-row">
                                    <TextFieldWrapper
                                        name="btnOptions"
                                        id="btnOptions"
                                        placeholder="Alan AI Button Options"
                                        value={btnOptions}
                                        autoComplete="off"
                                        onChange={this.handleBtnOptionsChange}
                                        onEnter={this.handleSave}
                                        onBlur={this.handleSave}
                                        skipOnChangeAfterOnBlur={true}
                                        error={btnOptionsErr}
                                        height={30}
                                    />
                                </div>
                            </div>}
                    </div>
                </div>
            </div>
        </PopupWrapper >;
    }
}

function mapStateToProps({ projectsInfo }: IStore) {
    return {
        isProjectOptionsSaving: projectsInfo.isProjectOptionsSaving,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        setProjectOptions: (param) => asyncDispatch(actions.setProjectOptions, dispatch, param),
        getButtonOptions: (param) => asyncDispatch(actions.getButtonOptions, dispatch, param),
    };
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AlanButtonExtensionForm);

